/**
 * CollectionColumnVisibilityDropdown - Thin wrapper over shared ColumnVisibilityDropdown
 * for the collection table. Connects to collection-specific settings atom.
 */
import type { ReactElement } from 'react';
import { useCallback } from 'react';

import { useAtom } from 'jotai';

import ColumnVisibilityDropdown from '@/components/ColumnVisibilityDropdown/ColumnVisibilityDropdown';
import {
  ALWAYS_VISIBLE_COLUMNS,
  COLLECTION_COLUMN_GROUPS,
  COLLECTION_COLUMN_LABELS,
  collectionTableSettingsAtom,
  DEFAULT_COLLECTION_COLUMN_VISIBILITY,
  getVisibleOptionalCount,
  resolveCollectionColumnVisibility,
  type CollectionColumnKey,
} from '@/features/collection/state/collectionTableSettings';

// ============================================================================
// Types
// ============================================================================

export interface CollectionColumnVisibilityDropdownProps {
  className?: string;
}

// ============================================================================
// Main Component
// ============================================================================

export default function CollectionColumnVisibilityDropdown({ className }: CollectionColumnVisibilityDropdownProps): ReactElement {
  const [settings, setSettings] = useAtom(collectionTableSettingsAtom);

  const toggleColumn = useCallback(
    (column: CollectionColumnKey) => {
      setSettings((prev) => {
        const visibility = resolveCollectionColumnVisibility(prev.columnVisibility);
        return {
          ...prev,
          columnVisibility: { ...visibility, [column]: !visibility[column] },
        };
      });
    },
    [setSettings],
  );

  const resetToDefaults = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      columnVisibility: DEFAULT_COLLECTION_COLUMN_VISIBILITY,
    }));
  }, [setSettings]);

  return (
    <ColumnVisibilityDropdown<CollectionColumnKey>
      visibility={resolveCollectionColumnVisibility(settings.columnVisibility)}
      onToggle={toggleColumn}
      onReset={resetToDefaults}
      groups={COLLECTION_COLUMN_GROUPS}
      labels={COLLECTION_COLUMN_LABELS}
      alwaysVisible={ALWAYS_VISIBLE_COLUMNS}
      activeCount={getVisibleOptionalCount(settings.columnVisibility)}
      className={className}
    />
  );
}
